import type Luminary from '@lumen5/luminary';
import type { OpaqueVideoData } from '@lumen5/luminary';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
    AssetTranscript,
    AudioAssetModel,
    SceneRecommendation,
    VideoPlan,
} from 'js/src/client';
import type { VideoPlanWithCreationInfo } from 'js/src/data/video-planner/video-planner-types';

export interface VideoPlannerState {
    videoPlans: VideoPlanWithCreationInfo[];
    selectedVideoPlan: string | null;
}

const initialState: VideoPlannerState = {
    videoPlans: [],
    selectedVideoPlan: null,
};

export const videoPlannerSlice = createSlice({
    name: 'videoPlanner',
    initialState,
    reducers: {
        pushVideoPlans: (
            state,
            action: PayloadAction<{
                videoPlans: VideoPlan[];
                referenceMaterial: string;
            }>,
        ) => {
            const newPlans = action.payload.videoPlans.map((plan) => {
                const planWithCreationInfo: VideoPlanWithCreationInfo = {
                    ...plan,
                    template_styling_video_data: null,
                    scene_recommendations: null,
                    voiceover: {
                        loading: false,
                        asset: null,
                    },
                    music_asset: null,
                    computed_duration_ms: null,
                    did_error_while_loading: false,
                    reference_material: action.payload.referenceMaterial,
                    luminaryInstance: null,
                    luminaryInstanceLoaded: false,
                };
                return planWithCreationInfo;
            });
            state.videoPlans.push(...newPlans);

            // Auto-select the first newly created video plan
            if (newPlans.length > 0) {
                state.selectedVideoPlan = newPlans[0].id;
            }
        },
        editVideoPlan: (
            state,
            action: PayloadAction<{
                videoPlan: VideoPlan;
            }>,
        ) => {
            const index = state.videoPlans.findIndex(
                (plan) => plan.id === action.payload.videoPlan.id,
            );
            if (index !== -1) {
                const existingPlan = state.videoPlans[index];
                existingPlan.luminaryInstance?.destroy();
                state.videoPlans[index] = {
                    ...action.payload.videoPlan,
                    id: existingPlan.id,
                    template_styling_video_data: null,
                    scene_recommendations: null,
                    voiceover: {
                        loading: false,
                        asset: null,
                    },
                    music_asset: null,
                    computed_duration_ms: null,
                    did_error_while_loading: false,
                    reference_material: existingPlan.reference_material,
                    luminaryInstance: null,
                    luminaryInstanceLoaded: false,
                };
            }
        },
        setVideoPlanTemplateStylingVideoData: (
            state,
            action: PayloadAction<{
                id: string;
                templateStylingVideoData: OpaqueVideoData;
            }>,
        ) => {
            const plan = state.videoPlans.find((p) => p.id === action.payload.id);
            if (plan) {
                plan.template_styling_video_data = action.payload.templateStylingVideoData;
            }
        },
        setVideoPlanSceneRecommendations: (
            state,
            action: PayloadAction<{
                id: string;
                sceneRecommendations: SceneRecommendation[];
            }>,
        ) => {
            const plan = state.videoPlans.find((p) => p.id === action.payload.id);
            if (plan) {
                plan.scene_recommendations = action.payload.sceneRecommendations;
            }
        },
        setVideoPlanLoadingError: (
            state,
            action: PayloadAction<{
                id: string;
            }>,
        ) => {
            const plan = state.videoPlans.find((p) => p.id === action.payload.id);
            if (plan) {
                plan.did_error_while_loading = true;
            }
        },
        setVideoPlanVoiceoverAsset: (
            state,
            action: PayloadAction<{
                id: string;
                voiceoverAsset: AudioAssetModel;
                transcript: AssetTranscript;
            }>,
        ) => {
            const plan = state.videoPlans.find((p) => p.id === action.payload.id);
            if (plan) {
                plan.voiceover.asset = action.payload.voiceoverAsset;
                plan.voiceover.transcript = action.payload.transcript;
                plan.voiceover.loading = false;
                plan.computed_duration_ms = action.payload.voiceoverAsset.duration * 1000;
            }
        },
        setVideoPlanMusicAsset: (
            state,
            action: PayloadAction<{
                id: string;
                musicAsset: AudioAssetModel;
            }>,
        ) => {
            const plan = state.videoPlans.find((p) => p.id === action.payload.id);
            if (plan) {
                plan.music_asset = action.payload.musicAsset;
            }
        },
        setVideoPlanVoiceoverLoading: (
            state,
            action: PayloadAction<{
                id: string;
                loading: boolean;
            }>,
        ) => {
            const plan = state.videoPlans.find((p) => p.id === action.payload.id);
            if (plan) {
                plan.voiceover.loading = action.payload.loading;
            }
        },
        setVideoPlanComputedDuration: (
            state,
            action: PayloadAction<{
                id: string;
                duration: number;
            }>,
        ) => {
            const plan = state.videoPlans.find((p) => p.id === action.payload.id);
            if (plan) {
                plan.computed_duration_ms = action.payload.duration;
            }
        },
        setSelectedVideoPlan: (state, action: PayloadAction<string | null>) => {
            state.selectedVideoPlan = action.payload;
        },
        setLuminaryInstance: (
            state,
            action: PayloadAction<{
                id: string;
                luminaryInstance: Luminary;
            }>,
        ) => {
            const plan = state.videoPlans.find((p) => p.id === action.payload.id);
            if (plan) {
                plan.luminaryInstance = action.payload.luminaryInstance;
            }
        },
        setLuminaryInstanceLoaded: (
            state,
            action: PayloadAction<{
                id: string;
                loaded: boolean;
            }>,
        ) => {
            const plan = state.videoPlans.find((p) => p.id === action.payload.id);
            if (plan) {
                plan.luminaryInstanceLoaded = action.payload.loaded;
            }
        },
        clearAllVideoPlans: (state) => {
            state.videoPlans.forEach((plan) => {
                plan.luminaryInstance?.destroy();
            });
            state.videoPlans = [];
            state.selectedVideoPlan = null;
        },
    },
});

export const {
    pushVideoPlans,
    editVideoPlan,
    setVideoPlanTemplateStylingVideoData,
    setVideoPlanSceneRecommendations,
    setVideoPlanLoadingError,
    setVideoPlanVoiceoverAsset,
    setVideoPlanMusicAsset,
    setVideoPlanVoiceoverLoading,
    setVideoPlanComputedDuration,
    setSelectedVideoPlan,
    setLuminaryInstance,
    setLuminaryInstanceLoaded,
    clearAllVideoPlans,
} = videoPlannerSlice.actions;

export const videoPlannerReducer = videoPlannerSlice.reducer;
